/**
 * PricingSection Component
 *
 * Pricing cards with different plans for patients and professionals.
 */

import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faUserMd, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';

// Register icons for this component
library.add(faUser, faUserMd, faCheck);

import { FadeIn } from './FadeIn';

export interface FeatureItem {
  text: string;
  bold?: boolean;
}

export interface PeriodPart {
  text: string;
  bold?: boolean;
  green?: boolean;
  badge?: boolean;
}

interface PricingCardProps {
  id: string;
  icon: IconDefinition;
  title: string;
  price: string;
  priceStrikethrough?: string;
  period: string | PeriodPart[];
  description: string;
  features: string[] | FeatureItem[];
  highlighted?: boolean;
  badge?: string;
  ctaText?: string;
  variant?: 'patient' | 'professional';
}

function PricingCard({
  id,
  icon,
  title,
  price,
  priceStrikethrough,
  period,
  description,
  features,
  highlighted = false,
  badge,
  ctaText = 'Começar Agora',
  variant = 'professional',
}: PricingCardProps) {
  return (
    <div
      id={id}
      className={`rounded-xl ${highlighted ? 'border-2 border-primary shadow-lg' : 'border shadow-sm'} bg-card text-card-foreground flex flex-col p-6 relative`}
    >
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-bold rounded-full">
          {badge}
        </div>
      )}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <FontAwesomeIcon icon={icon} className="text-2xl text-primary" />
          <h3 className="font-bold text-xl">{title}</h3>
        </div>
        <div className="flex items-baseline gap-1 mt-2">
          <div className={`text-3xl font-bold ${variant === 'professional' ? 'text-primary' : ''}`}>{price}</div>
          {price !== 'Grátis' && <span className="text-sm font-medium text-muted-foreground">/mês</span>}
        </div>
        {priceStrikethrough && (
          <div className="flex items-baseline gap-1 mb-1">
            <div className="text-lg line-through text-red-400 font-medium">{priceStrikethrough}</div>
            <span className="text-xs font-medium text-red-400 line-through">/mês</span>
          </div>
        )}
        <div
          className={`text-sm text-muted-foreground mt-2 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 border border-red-100 dark:border-red-900/30 p-2 rounded-lg inline-block`}
        >
          {typeof period === 'string' ? (
            period
          ) : (
            period.map((part, index) => {
              if (part.badge) {
                return (
                  <span
                    key={index}
                    className="bg-red-500 text-white px-2 py-0.5 rounded shadow-sm text-xs font-black uppercase tracking-wider animate-pulse"
                  >
                    {part.text}
                  </span>
                );
              }
              if (part.bold && part.green) {
                return (
                  <span key={index} className="text-green-600 font-bold">
                    {part.text}
                  </span>
                );
              }
              if (part.bold) {
                return <strong key={index}>{part.text}</strong>;
              }
              return <span key={index}>{part.text}</span>;
            })
          )}
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-4">{description}</p>
      <ul className="space-y-2 mb-6 flex-1 text-sm text-muted-foreground">
        {features.map((feature, index) => {
          const featureItem = typeof feature === 'string' ? { text: feature } : feature;
          return (
            <li key={index} className="flex items-center">
              <FontAwesomeIcon
                icon={faCheck}
                className={`${variant === 'patient' ? 'text-secondary' : 'text-primary'} mr-2`}
              />
              {featureItem.bold ? <strong>{featureItem.text}</strong> : <span>{featureItem.text}</span>}
            </li>
          );
        })}
      </ul>
      <Link
        to="/user/signup"
        className={`inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${variant === 'professional'
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
          } h-10 px-4 w-full`}
      >
        {ctaText}
      </Link>
    </div>
  );
}

export function PricingSection() {
  return (
    <>
      {/* Pricing Intro Section */}
      <section className="container pt-16 pb-8 sm:pt-24 sm:pb-12 lg:pt-32 lg:pb-16 space-y-8">
        <FadeIn className="mx-auto flex max-w-[58rem] flex-col items-center justify-center gap-4 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-balance">
            Planos para <span className="text-primary">Pacientes</span> e <span className="text-secondary">Profissionais</span>
          </h2>
          <p className="max-w-[42rem] leading-normal text-muted-foreground sm:text-xl sm:leading-8 text-balance">
            Comece gratuitamente e evolua conforme sua necessidade. Cancele quando quiser.
          </p>
        </FadeIn>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="container pb-16 md:pb-24 lg:pb-32">
        <FadeIn className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Patient Free */}
          <PricingCard
            id="paciente"
            icon={faUser}
            title="Paciente"
            price="Grátis"
            period="Para sempre"
            description="Para pacientes que querem organizar exames, histórico médico e compartilhar com profissionais."
            features={[
              'Armazenamento de Exames',
              'Histórico digital completo',
              'Aplicativo Mobile',
              'Conforme LGPD',
              'Extração de dados com IA (limitado)',
            ]}
            variant="patient"
          />

          {/* Professional Basic */}
          <PricingCard
            id="profissional-basico"
            icon={faUserMd}
            title="Profissional Básico"
            price="R$ 100,00"
            priceStrikethrough="R$ 125,00"
            period={[
              { text: 'Período de teste gratuito', bold: true, green: true }
            ]}
            description="Para profissionais que precisam de um sistema para organizar exames e pacientes."
            features={[
              { text: 'Tudo do Paciente', bold: true },
              { text: 'Extração de dados de exames com IA' },
              { text: 'Receitas Digitais' },
              { text: 'Gestão de Pacientes' },
              { text: 'Acesso para Secretariado' },
            ] as FeatureItem[]}
            variant="professional"
          />

          {/* Professional Advanced */}
          <PricingCard
            id="profissional-avancado"
            icon={faUserMd}
            title="Profissional Avançado"
            price="R$ 250,00"
            priceStrikethrough="R$ 312,50"
            period={[
              { text: 'Período de teste gratuito', bold: true, green: true }
            ]}
            description="Para médicos que querem o poder máximo da inteligência artificial."
            features={[
              { text: 'Tudo do Básico', bold: true },
              { text: 'Extração de dados com IA (maior quota)' },
              { text: 'Transcrição Automática de Consultas' },
              { text: 'Assistente/Chatbot IA' },
            ] as FeatureItem[]}
            highlighted={true}
            badge="RECOMENDADO"
            variant="professional"
          />
        </FadeIn>
      </section>
    </>
  );
}
